import type { CSSProperties, ReactNode } from 'react';
import styled from 'styled-components';

import { Vertical } from '#src/atoms/layout/flex/index.tsx';

type ShieldShapeOrientation = 'up' | 'down';
type ShieldShapeSize = 'sm' | 'md' | 'lg' | 'xl';

const ORIENTATION_ROTATION: Record<ShieldShapeOrientation, number> = {
  up: 0,
  down: 180,
};

const ICON_INSET: Record<ShieldShapeOrientation, CSSProperties> = {
  down: { top: 0, right: 0, bottom: '10%', left: 0 },
  up: { top: '10%', right: 0, bottom: 0, left: 0 },
};

const SIZE_PX: Record<ShieldShapeSize, number> = {
  sm: 48,
  md: 64,
  lg: 80,
  xl: 96,
};

const SHAPE_PATH =
  'M8.71313 61.5947L53.0227 61.5947C54.3601 61.5947 55.609 60.9262 56.3508 59.8135L61.5422 52.0274C62.3644 50.794 62.4367 49.2067 61.7307 47.9033L37.4592 3.09473C36.7601 1.80418 35.4104 1.00013 33.9426 1.00001L29.4661 1.00001C27.9981 1.00001 26.6477 1.80396 25.9485 3.09473L1.48267 48.2627C0.860998 49.4104 0.838396 50.7896 1.42212 51.957L5.13501 59.3838C5.81257 60.7389 7.19806 61.5947 8.71313 61.5947Z';

const Root = styled.div`
  position: relative;
  flex-shrink: 0;
  width: var(--shape-size);
  height: var(--shape-size);
`;

const ShapeSvg = styled.svg`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
`;

const IconWrapper = styled(Vertical).attrs({ center: true })`
  position: absolute;
  justify-content: center;
`;

type Props = {
  borderColor?: string;
  background?: string;
  icon?: ReactNode;
  orientation?: ShieldShapeOrientation;
  size?: ShieldShapeSize;
  className?: string;
  style?: CSSProperties;
};

export const ShieldShape = ({
  borderColor = 'var(--c-nd-150)',
  background = 'var(--c-nl-600)',
  icon,
  orientation = 'down',
  size = 'md',
  className,
  style,
}: Props) => {
  const rotation = ORIENTATION_ROTATION[orientation];
  const sizePx = SIZE_PX[size];

  return (
    <Root
      className={className}
      style={{ '--shape-size': `${sizePx}px`, ...style } as CSSProperties}
    >
      <ShapeSvg
        viewBox="0 0 64 63"
        xmlns="http://www.w3.org/2000/svg"
        style={
          rotation !== 0 ? { transform: `rotate(${rotation}deg)` } : undefined
        }
      >
        <path
          d={SHAPE_PATH}
          fill={background}
          stroke={borderColor}
          strokeWidth={4}
          paintOrder="stroke fill"
          vectorEffect="non-scaling-stroke"
        />
      </ShapeSvg>
      {!!icon && (
        <IconWrapper style={ICON_INSET[orientation]}>{icon}</IconWrapper>
      )}
    </Root>
  );
};
